/**
 * Bank-Matching-Inbox
 * ===================
 * Zeigt alle automatisch vorgeschlagenen WEG-JA-Bank-Matches, die vom
 * Verwalter noch bestätigt oder abgelehnt werden müssen. Jede Zeile
 * enthält Betrag/Datum/Betreff für WEG-Entry + Bank-TX, einen Match-Score,
 * die Differenz und Confirm/Reject-Buttons.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Bank, Check, X, ArrowClockwise, CheckCircle, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import { formatDate, formatEUR } from "@/lib/constants";

export default function BankInbox() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/bank-match-inbox");
      setItems(data?.items || []);
    } catch (e) {
      toast.error("Inbox konnte nicht geladen werden");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const confirm = async (entryId) => {
    try {
      await api.post(`/iter114/bank-match-inbox/${entryId}/confirm`);
      toast.success("Match bestätigt");
      setItems((cur) => cur.filter((i) => i.entry_id !== entryId));
    } catch (e) { toast.error("Fehler"); }
  };
  const reject = async (entryId) => {
    if (!await window._domivusConfirm("Match wirklich ablehnen? Die Bank-Zuordnung wird entfernt.")) return;
    try {
      await api.post(`/bank-match-inbox/${entryId}/reject`);
      toast.success("Match abgelehnt");
      setItems((cur) => cur.filter((i) => i.entry_id !== entryId));
    } catch (e) { toast.error("Fehler"); }
  };

  return (
    <div className="space-y-4" data-testid="bank-inbox-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900 flex items-center gap-2">
            <Bank size={24} className="text-emerald-700" />
            Bank-Matching-Inbox
          </h1>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            Prüfen Sie alle automatisch vorgeschlagenen Zuordnungen von Bank-Transaktionen zu
            WEG-Jahresabrechnungs-Buchungen. Nach Freigabe fließen die Beträge fest in die JA ein.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} className="gap-1"><ArrowClockwise size={12} /> Neu laden</Button>
          <div className="text-xs text-slate-500">{loading ? "Lade…" : `${items.length} offen`}</div>
        </div>
      </div>

      {!loading && items.length === 0 && (
        <div className="rounded-xl border-2 border-dashed border-slate-200 py-14 text-center bg-slate-50/50">
          <CheckCircle size={40} className="mx-auto text-emerald-400 mb-3" weight="fill" />
          <div className="text-slate-600 mb-1 font-medium">Alles freigegeben.</div>
          <div className="text-xs text-slate-400">
            Aktuell warten keine automatisch vorgeschlagenen Bank-Zuordnungen auf Ihre Prüfung.
          </div>
        </div>
      )}

      {items.length > 0 && (
        <div className="space-y-2">
          {items.map((it) => (
            <MatchRow key={it.entry_id} item={it} onConfirm={confirm} onReject={reject} />
          ))}
        </div>
      )}
    </div>
  );
}

function MatchRow({ item: it, onConfirm, onReject }) {
  const scoreCls = it.match_score >= 90 ? "bg-emerald-100 text-emerald-800 border-emerald-200"
                : it.match_score >= 75 ? "bg-amber-100 text-amber-800 border-amber-200"
                : "bg-rose-100 text-rose-800 border-rose-200";
  const diff = Math.abs(it.amount_diff || 0);
  return (
    <div
      className="rounded-lg border border-slate-200 bg-white p-3 grid grid-cols-1 md:grid-cols-12 gap-3 items-center"
      data-testid={`bank-inbox-row-${it.entry_id}`}
    >
      {/* Objekt */}
      <div className="md:col-span-2">
        <div className="text-[10px] uppercase tracking-wider text-slate-400">Objekt · JA</div>
        <div className="text-sm font-medium text-slate-800 truncate">{it.property_name || "—"}</div>
        <div className="text-[10px] text-slate-500 tabular-nums">
          {it.period_from ? formatDate(it.period_from) : ""} – {it.period_to ? formatDate(it.period_to) : ""}
        </div>
      </div>

      {/* WEG-Entry */}
      <div className="md:col-span-4 border-l border-slate-100 pl-3">
        <div className="text-[10px] uppercase tracking-wider text-slate-400">WEG-Buchung</div>
        <div className="text-sm text-slate-800 truncate">{it.category || "—"}</div>
        <div className="text-xs text-slate-500 tabular-nums">
          {formatEUR(it.entry_amount)} · {it.entry_date ? formatDate(it.entry_date) : ""}
          {it.entry_supplier ? ` · ${it.entry_supplier}` : ""}
        </div>
      </div>

      {/* Bank-TX */}
      <div className="md:col-span-4 border-l border-slate-100 pl-3">
        <div className="text-[10px] uppercase tracking-wider text-slate-400">Bank-Transaktion</div>
        <div className="text-sm text-slate-800 truncate">{it.tx_counterparty || "—"}</div>
        <div className="text-xs text-slate-500 tabular-nums truncate">
          {formatEUR(it.tx_amount)} · {it.tx_date ? formatDate(it.tx_date) : ""}
        </div>
        {it.tx_purpose && (
          <div className="text-[10px] text-slate-400 truncate italic mt-0.5">{it.tx_purpose}</div>
        )}
      </div>

      {/* Score + Diff + Actions */}
      <div className="md:col-span-2 flex flex-col items-end gap-1">
        <div className="flex items-center gap-1.5">
          <span className={`text-[10px] px-1.5 py-0.5 rounded border ${scoreCls}`}>
            Score {Math.round(it.match_score)}
          </span>
          {diff > 0.01 && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200 inline-flex items-center gap-0.5">
              <Warning size={9} weight="fill" /> ±{formatEUR(diff)}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <Button
            size="sm"
            className="h-7 bg-emerald-600 hover:bg-emerald-700 gap-1"
            onClick={() => onConfirm(it.entry_id)}
            data-testid={`bank-inbox-confirm-${it.entry_id}`}
          >
            <Check size={12} /> Freigeben
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-7 border-rose-200 text-rose-600 hover:bg-rose-50 gap-1"
            onClick={() => onReject(it.entry_id)}
            data-testid={`bank-inbox-reject-${it.entry_id}`}
          >
            <X size={12} /> Ablehnen
          </Button>
        </div>
      </div>
    </div>
  );
}
